import {
  breakpointSizes,
  getBreakpointDown,
  getFontCss,
  styleConstants,
  themeConstants,
} from '@paywithextend/components'
import moment, { Moment } from 'moment'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { useLocation } from 'react-router'
import { selectAllUsersFromState } from 'src/app/AccountTransactions/redux/accountTransactions.selectors'
import { CreditCardDropdownOptionDetails } from 'src/components/CreditCardDropdownV2/CreditCardDropdownOption'
import { DropdownValue } from 'src/components/CreditCardDropdownV2/CreditCardDropdownV2'
import UtilityToolbar from 'src/components/DataGrid/UtilityToolbar/UtilityToolbar'
import UtilityToolbarDatePicker from 'src/components/DataGrid/UtilityToolbar/UtilityToolbarDatePicker'
import DateFilterDropdown, {
  DateRangeType,
} from 'src/components/DateFilterDropdown/DateFilterDropdown'
import DividerLineDiv from 'src/components/DividerLineDiv/DividerLineDiv'
import ReceiptToolsDropdownUtilityToolbar from 'src/components/ReceiptToolsDropdown/ReceiptToolsDropdownUtilityToolbar'
import { TransactionReportType } from 'src/components/ReportTransactionsBoxAndLinks/reportTypes'
import SvgCalendarNew from 'src/components/Svgs/SvgCalendarNew'
import Tooltip from 'src/components/Tooltip/Tooltip'
import {
  AccountFilter,
  CardUserFilter,
  DepartmentsFilter,
  ExpenseCategoryLabelsFilter,
  ExpenseCategoryStatusFilter,
  TransactionAmountRangeFilter,
  TransactionDateTypeFilter,
  TransactionMerchantCategoryFilter,
  TransactionOriginFilter,
  TransactionReceiptStatusFilter,
  TransactionReviewStatusFilter,
  TransactionStatusFilter,
  TransactionSyncStatusFilter,
} from 'src/components/TransactionsListFilter/filters'
import ReceiptSyncStatusFilter from 'src/components/TransactionsListFilter/filters/ReceiptSyncStatusFilter'
import {
  TransactionsFilters,
  TransactionsFiltersBody,
} from 'src/components/TransactionsListFilter/transactionsFilter.schema'
import { TransactionFilterOptions } from 'src/enums'
import {
  FormFieldsValidation,
  useFormFields,
  useStringSetState,
} from 'src/hooks'
import { useExtendLocation } from 'src/hooks/locationHooks'
import { useDropdownInfiniteScroll } from 'src/hooks/useDropdownInfiniteScroll'
import Trns, {
  translateSingleString,
  useTranslate,
} from 'src/localization/Trns'
import {
  OrgExpenseReportCycleGroup,
  OrgExpenseReportCycleModel,
} from 'src/models/orgExpenseReports.models'
import { TransactionDateType } from 'src/models/transaction.models'
import { useQueryDepartments } from 'src/queries/organizations/department.queries'
import { useQueryExpenseCategories } from 'src/queries/organizations/expenseCategories.queries'
import {
  issuerGroupedRCNOrgExpenseReportCycles,
  useQueryOrgExpenseReportCycleReports,
  useQueryOrgExpenseReportCycles,
} from 'src/queries/orgExpenseReports.queries'
import { defaultQueryCacheOptions } from 'src/queries/queries.constants'
import { useQueryUsersSearch } from 'src/queries/users.queries'
import {
  selectNullSafeLocaleFromState,
  selectUserListModelFromState,
} from 'src/redux/selectors/auth.selectors'
import { selectFeatureFlaggerFromState } from 'src/redux/selectors/data/featureFlagger.selectors'
import {
  selectOrganizationReceiptMgmtEnabledFromState,
  selectUserExplicitOrganizationListModelFromState,
} from 'src/redux/selectors/data/organizations.selectors'
import { selectActiveOrgIntegration } from 'src/redux/selectors/data/orgIntegrations.selectors'
import { css } from 'styled-components'

interface TransactionsListFilterProps {
  defaultFilters: TransactionsFilters // only used for resetting single filters without immediately applying the change
  initialFilterValues: TransactionsFilters
  filterValues: TransactionsFilters
  selectedFiltersCount: number
  handleFilterChange: (newFilters: Partial<TransactionsFilters>) => void
  resetFilters: () => void
  resetSingleFilter: (key: keyof TransactionsFilters) => void
  apiQueryParams: TransactionsFiltersBody // for txn download
  viewingReport?: boolean // to handle non-navigation changes for org expense report quick filter buttons
  reportId?: string
  hasReceiptToolsDropdown?: boolean
  context: TransactionReportType
  visibleFilters: TransactionFilterOptions[]
  isTransactionsFetching?: boolean
  handleDownloadClick?: () => void
}

const TransactionsListFilter: React.FC<TransactionsListFilterProps> = (
  props,
): React.ReactElement => {
  const translate = useTranslate()
  const extendLocation = useExtendLocation()
  const location = useLocation()
  const locale = useSelector(selectNullSafeLocaleFromState)
  const user = useSelector(selectUserListModelFromState)
  const activeIntegration = useSelector(selectActiveOrgIntegration)
  const {
    show_automated_expense_review: showAutomatedExpenseReview,
    show_bulk_erp_attachments: showBulkErpAttachments,
  } = useSelector(selectFeatureFlaggerFromState)

  // FETCHING DATA FOR FILTER DROPDOWNS -------------------------------------

  // expense category labels
  const { data: expenseCategories } = useQueryExpenseCategories({
    filters: { active: true },
  })
  const requiredExpenseCategories =
    expenseCategories &&
    expenseCategories.filter((category) => category.requiredForTransactions)
      .length > 0

  // receipt management
  const receiptManagementEnabled = useSelector(
    selectOrganizationReceiptMgmtEnabledFromState,
  )

  // users
  const recipientOptions = useSelector(selectAllUsersFromState)
  const [userSearch, setUserSearch] = useState('')

  const {
    data: usersData,
    isLoading,
    isFetchingNextPage,
    fetchNextPage,
    complete: usersComplete,
  } = useQueryUsersSearch({
    filters: {
      q: userSearch !== '' ? userSearch : undefined,
      count: 25,
    },
    queryCacheOptions: defaultQueryCacheOptions,
    additionalKey: extendLocation.matchedPath,
  })

  const userInfiniteScrollProps = useDropdownInfiniteScroll({
    currentPage: usersData?.page,
    isLoading,
    isFetchingNextPage,
    isComplete: usersComplete,
    fetchNextPage,
    currentSearch: userSearch,
    setSearch: setUserSearch,
  })

  // teams
  const organization = useSelector(
    selectUserExplicitOrganizationListModelFromState,
  )
  const { data: departmentsList } = useQueryDepartments({
    organizationId: organization?.id,
    includeArchived: false,
  })
  const departmentsOptions = useMemo(
    () =>
      departmentsList?.map((department) => ({
        value: department.id,
        details: {
          text: department.name,
        },
      })) ?? [],
    [departmentsList],
  )

  // ------------------------------------------------------------------------

  // STATE TRACKERS FOR UI FILTERS ------------------------------------------

  const [searchFilter, setSearchFilter] = useState(
    props.filterValues.search || '',
  )
  const [dateType, setDateType] = useState(props.initialFilterValues.dateType)
  const [startDateFilter, setStartDateFilter] = useState(
    props.initialFilterValues.since,
  )
  const [endDateFilter, setEndDateFilter] = useState(
    props.initialFilterValues.until,
  )
  const [minClearingBillingCentsFilter, setMinClearingBillingCentsFilter] =
    useState<number | undefined>(
      props.initialFilterValues.minClearingBillingCents !== undefined
        ? Number(props.initialFilterValues.minClearingBillingCents)
        : undefined,
    )
  const [maxClearingBillingCentsFilter, setMaxClearingBillingCentsFilter] =
    useState<number | undefined>(
      props.initialFilterValues.maxClearingBillingCents !== undefined
        ? Number(props.initialFilterValues.maxClearingBillingCents)
        : undefined,
    )
  const {
    set: statusSet,
    setSet: setStatusSet,
    handleSelection: handleStatusSelection,
  } = useStringSetState(props.initialFilterValues.status)
  const {
    set: originSet,
    setSet: setOriginSet,
    handleSelection: handleOriginSelection,
  } = useStringSetState(props.initialFilterValues.source)
  const {
    set: expenseCategoryStatusesSet,
    setSet: setExpenseCategoryStatusesSet,
    handleSelection: handleExpenseCategoryStatusesSelection,
  } = useStringSetState(props.initialFilterValues.expenseCategoryStatuses)
  const {
    fields: expenseCategoriesSet,
    setFields: setExpenseCategoriesFields,
    onFieldChange: handleExpenseCategoriesSelection,
  } = useFormFields<Record<string, Set<string>>>(
    props.initialFilterValues.expenseCategories || {},
    (): FormFieldsValidation<Record<string, Set<string>>> =>
      ({}) as FormFieldsValidation<Record<string, Set<string>>>,
  )
  const {
    set: receiptStatusSet,
    setSet: setReceiptStatusSet,
    handleSelection: handleReceiptStatusSelection,
  } = useStringSetState(props.initialFilterValues.receiptStatus)
  const [receiptRequired, setReceiptRequired] = useState<boolean | undefined>(
    props.initialFilterValues.receiptRequired,
  )
  const {
    set: syncStatusesSet,
    setSet: setSyncStatusesSet,
    handleSelection: handleSyncStatusesSelection,
  } = useStringSetState(props.initialFilterValues.syncStatuses)
  const {
    set: receiptAttachmentSyncStatusesSet,
    setSet: setReceiptAttachmentSyncStatusesSet,
    handleSelection: handleReceiptAttachmentSyncStatusesSelection,
  } = useStringSetState(props.initialFilterValues.receiptAttachmentSyncStatuses)
  const {
    set: recipientSet,
    setSet: setRecipientSet,
    handleSelection: handleRecipientSelection,
  } = useStringSetState(props.initialFilterValues.recipient)
  const {
    set: departmentsSet,
    setSet: setDepartmentsSet,
    handleSelection: handleDepartmentsSelection,
  } = useStringSetState(props.initialFilterValues.departments)
  const {
    set: creditCardSet,
    setSet: setCreditCardSet,
    handleSelection: handleCreditCardSelection,
  } = useStringSetState(props.initialFilterValues.creditCardId)
  const {
    set: mccGroupSet,
    setSet: setMccGroupSet,
    handleSelection: handleMccGroupSelection,
  } = useStringSetState(props.initialFilterValues.mccGroup)
  const {
    set: reviewStatusSet,
    setSet: setReviewStatusSet,
    handleSelection: handleReviewStatusesSelection,
  } = useStringSetState(props.initialFilterValues.reviewStatus)

  // dates & cycles
  const { data: cycles } = useQueryOrgExpenseReportCycles({
    select: issuerGroupedRCNOrgExpenseReportCycles,
    enabled: props.context === TransactionReportType.AccountingTransactions,
  })

  const selectedCreditCardCycle = cycles
    ?.flatMap((group: OrgExpenseReportCycleGroup) => group.rcns)
    .filter(
      (cycle: OrgExpenseReportCycleModel) =>
        cycle.accountId === creditCardSet.values().next().value,
    )[0]

  const { data: cycleReports } = useQueryOrgExpenseReportCycleReports({
    cycleId: selectedCreditCardCycle?.id,
    enabled: !!selectedCreditCardCycle?.cycleType,
  })

  const sortedStatements =
    cycleReports?.sort(
      (a, b) => new Date(b.until).getTime() - new Date(a.until).getTime(),
    ) || []
  const currentStatement =
    sortedStatements.length > 0 ? sortedStatements[0] : undefined
  const previousStatement =
    sortedStatements.length > 1 ? sortedStatements[1] : undefined

  const deriveDateRangeType = (
    since?: Moment,
    until?: Moment,
  ): DateRangeType | undefined => {
    if (!since || !until) return undefined

    const curSince = currentStatement?.since
      ? moment(currentStatement.since).startOf('day')
      : undefined
    const curUntil = currentStatement?.until
      ? moment(currentStatement.until).endOf('day')
      : undefined
    const prevSince = previousStatement?.since
      ? moment(previousStatement.since).startOf('day')
      : undefined
    const prevUntil = previousStatement?.until
      ? moment(previousStatement.until).endOf('day')
      : undefined

    const sameRange = (
      aS: moment.Moment,
      aE: moment.Moment,
      bS?: moment.Moment,
      bE?: moment.Moment,
    ) => !!bS && !!bE && aS.isSame(bS, 'day') && aE.isSame(bE, 'day')

    if (sameRange(since, until, curSince, curUntil))
      return DateRangeType.CurrentStatement
    if (sameRange(since, until, prevSince, prevUntil))
      return DateRangeType.PreviousStatement

    const todayStart = moment().startOf('day')
    const todayEnd = moment().endOf('day')

    const thisMonthStart = moment().startOf('month')
    const thisMonthEnd = moment().endOf('month')

    const lastMonthStart = moment().subtract(1, 'month').startOf('month')
    const lastMonthEnd = moment().subtract(1, 'month').endOf('month')

    const last30Start = moment().subtract(30, 'days').startOf('day')
    const last30End = todayEnd

    if (since.isSame(todayStart, 'day') && until.isSame(todayEnd, 'day')) {
      return DateRangeType.Today
    }
    if (
      since.isSame(thisMonthStart, 'day') &&
      until.isSame(thisMonthEnd, 'day')
    ) {
      return DateRangeType.ThisMonth
    }
    if (
      since.isSame(lastMonthStart, 'day') &&
      until.isSame(lastMonthEnd, 'day')
    ) {
      return DateRangeType.LastMonth
    }
    if (
      since.isSame(last30Start, 'day') &&
      (!until || until.isSame(last30End, 'day'))
    ) {
      return undefined
    }
    return DateRangeType.Custom
  }

  const [dateRangeType, setDateRangeType] = useState<DateRangeType | undefined>(
    () =>
      deriveDateRangeType(props.filterValues.since, props.filterValues.until),
  )

  useEffect(() => {
    const next = deriveDateRangeType(
      props.filterValues.since,
      props.filterValues.until,
    )
    setDateRangeType(next)
  }, [props.filterValues.since, props.filterValues.until])

  const customDateText =
    startDateFilter !== undefined
      ? endDateFilter !== undefined
        ? `${startDateFilter.format('M/D/YY')} - ${endDateFilter?.format('M/D/YY') || ''}`
        : `After ${startDateFilter.format('M/D/YY')}`
      : endDateFilter !== undefined
        ? `On or before ${endDateFilter.format('M/D/YY')}`
        : `Choose a date range`

  let dateRangeText = ''
  switch (dateRangeType) {
    case DateRangeType.Today:
    case DateRangeType.ThisMonth:
    case DateRangeType.LastMonth:
      dateRangeText = translate(dateRangeType)
      break
    case DateRangeType.Custom:
      dateRangeText = customDateText
      break
    case DateRangeType.CurrentStatement:
      if (currentStatement && creditCardSet.size === 1) {
        dateRangeText = translate('Current Statement')
      } else {
        dateRangeText = customDateText
      }
      break
    case DateRangeType.PreviousStatement:
      if (previousStatement && creditCardSet.size === 1) {
        dateRangeText = translate('Previous Statement')
      } else {
        dateRangeText = customDateText
      }
      break
    default:
      dateRangeText = translate('Last 30 Days')
      break
  }

  const handleSetType = (type: DateRangeType | undefined): void => {
    setDateRangeType(type)
    if (!type) {
      props.handleFilterChange({
        since: props.defaultFilters.since,
        until: props.defaultFilters.until,
      })
    }
  }

  const handleSetRange = (since: Moment, until: Moment): void => {
    setStartDateFilter(since)
    setEndDateFilter(until)
    props.handleFilterChange({ since: since?.clone(), until: until?.clone() })
  }

  const handleSetCreditCardId = (
    value: DropdownValue<CreditCardDropdownOptionDetails>,
  ): void => {
    const next = new Set(creditCardSet)
    if (value.selected) {
      next.add(value.value)
    } else {
      next.delete(value.value)
    }
    setCreditCardSet(next)
    props.handleFilterChange({ creditCardId: next })
  }

  const handleSetSince = (val: Moment | undefined): void => {
    setStartDateFilter(val)
    props.handleFilterChange({ since: val?.clone() })
  }

  const handleSetUntil = (val: Moment | undefined): void => {
    setEndDateFilter(val)
    props.handleFilterChange({ until: val?.clone() })
  }

  const resetCreditCardSet = (): void => {
    setCreditCardSet(new Set())
    props.resetSingleFilter('creditCardId')
  }

  useEffect(() => {
    if (creditCardSet.size !== 1) return
    if (!dateRangeType) return
    if (
      dateRangeType &&
      ![
        DateRangeType.CurrentStatement,
        DateRangeType.PreviousStatement,
      ].includes(dateRangeType)
    )
      return

    const target =
      dateRangeType === DateRangeType.CurrentStatement
        ? currentStatement
        : previousStatement

    if (!target?.since || !target?.until) return

    const since = moment(target.since).startOf('day')
    const until = moment(target.until).endOf('day')

    const alreadyApplied =
      startDateFilter?.isSame(since, 'day') &&
      endDateFilter?.isSame(until, 'day')

    if (!alreadyApplied) {
      handleSetRange(since, until)
    }
  }, [
    dateRangeType,
    currentStatement,
    previousStatement,
    creditCardSet,
    startDateFilter,
    endDateFilter,
    handleSetRange,
  ])

  // ------------------------------------------------------------------------

  const resetUIFiltersToCurrent = useCallback((): void => {
    setSearchFilter(props.filterValues.search || '')
    setDateType(props.filterValues.dateType)
    setStartDateFilter(props.filterValues.since)
    setEndDateFilter(props.filterValues.until)
    setMinClearingBillingCentsFilter(props.filterValues.minClearingBillingCents)
    setMaxClearingBillingCentsFilter(props.filterValues.maxClearingBillingCents)
    setStatusSet(props.filterValues.status || new Set<string>())
    setOriginSet(props.filterValues.source || new Set<string>())
    setExpenseCategoryStatusesSet(
      props.filterValues.expenseCategoryStatuses || new Set<string>(),
    )
    setExpenseCategoriesFields(props.filterValues.expenseCategories || {})
    setReceiptStatusSet(props.filterValues.receiptStatus || new Set<string>())
    setReceiptRequired(props.filterValues.receiptRequired)
    setSyncStatusesSet(props.filterValues.syncStatuses || new Set<string>())
    setReceiptAttachmentSyncStatusesSet(
      props.filterValues.receiptAttachmentSyncStatuses || new Set<string>(),
    )
    setRecipientSet(props.filterValues.recipient || new Set<string>())
    setDepartmentsSet(props.filterValues.departments || new Set<string>())
    setCreditCardSet(props.filterValues.creditCardId || new Set<string>())
    setMccGroupSet(props.filterValues.mccGroup || new Set<string>())
    setReviewStatusSet(props.filterValues.reviewStatus || new Set<string>())
  }, [props.filterValues])

  const filtersList: React.ReactElement[] = useMemo(() => {
    const filters: React.ReactElement[] = []

    // Date Filter (Date Type + Date Range)
    if (props.visibleFilters.includes(TransactionFilterOptions.Date)) {
      filters.push(
        <TransactionDateTypeFilter
          key="date-filter"
          dateType={dateType || TransactionDateType.Transaction}
          setDateType={setDateType}
          startDateFilter={startDateFilter}
          setStartDateFilter={setStartDateFilter}
          endDateFilter={endDateFilter}
          setEndDateFilter={setEndDateFilter}
          filterValues={props.filterValues}
          isTransactionsFetching={props.isTransactionsFetching}
          showDateRange={props.visibleFilters.includes(
            TransactionFilterOptions.TransactionDateRange,
          )}
        />,
      )
    }

    // Transaction Amount Range Filter
    if (
      props.visibleFilters.includes(
        TransactionFilterOptions.TransactionAmountRange,
      )
    ) {
      filters.push(
        <TransactionAmountRangeFilter
          key="amount-range-filter"
          minClearingBillingCentsFilter={minClearingBillingCentsFilter}
          setMinClearingBillingCentsFilter={setMinClearingBillingCentsFilter}
          maxClearingBillingCentsFilter={maxClearingBillingCentsFilter}
          setMaxClearingBillingCentsFilter={setMaxClearingBillingCentsFilter}
        />,
      )
    }

    // Transaction Status Filter
    if (
      props.visibleFilters.includes(TransactionFilterOptions.TransactionStatus)
    ) {
      filters.push(
        <TransactionStatusFilter
          key="status-filter"
          statusSet={statusSet}
          handleStatusSelection={handleStatusSelection}
        />,
      )
    }

    // Transaction Origin Filter
    if (
      props.visibleFilters.includes(TransactionFilterOptions.TransactionOrigin)
    ) {
      filters.push(
        <TransactionOriginFilter
          key="origin-filter"
          originSet={originSet}
          handleOriginSelection={handleOriginSelection}
        />,
      )
    }

    // Expense Category Status Filter
    if (
      (activeIntegration !== undefined || requiredExpenseCategories) &&
      props.visibleFilters.includes(
        TransactionFilterOptions.ExpenseCategoryStatus,
      )
    ) {
      filters.push(
        <ExpenseCategoryStatusFilter
          key="expense-category-status-filter"
          expenseCategoryStatusesSet={expenseCategoryStatusesSet}
          handleExpenseCategoryStatusesSelection={
            handleExpenseCategoryStatusesSelection
          }
        />,
      )
    }

    // Expense Category Labels Filter
    if (
      props.visibleFilters.includes(
        TransactionFilterOptions.ExpenseCategoryLabels,
      ) &&
      expenseCategories &&
      expenseCategories.length > 0
    ) {
      filters.push(
        <ExpenseCategoryLabelsFilter
          key="expense-category-labels-filter"
          expenseCategories={expenseCategories}
          expenseCategoriesSet={expenseCategoriesSet}
          handleExpenseCategoriesSelection={handleExpenseCategoriesSelection}
        />,
      )
    }

    // Receipt Status Filter
    if (props.visibleFilters.includes(TransactionFilterOptions.ReceiptStatus)) {
      filters.push(
        <TransactionReceiptStatusFilter
          key="receipt-status-filter"
          receiptStatusSet={receiptStatusSet}
          handleReceiptStatusSelection={handleReceiptStatusSelection}
          receiptRequired={receiptRequired}
          setReceiptRequired={setReceiptRequired}
        />,
      )
    }

    // Review Status Filter
    if (props.visibleFilters.includes(TransactionFilterOptions.ReviewStatus)) {
      filters.push(
        <TransactionReviewStatusFilter
          key="review-status-filter"
          reviewStatusSet={reviewStatusSet}
          handleReviewStatusesSelection={handleReviewStatusesSelection}
        />,
      )
    }

    // TODO: modify UI of these filters

    // Transaction Sync Status Filter
    if (
      props.visibleFilters.includes(
        TransactionFilterOptions.TransactionSyncStatus,
      )
    ) {
      filters.push(
        <TransactionSyncStatusFilter
          key="transaction-sync-status-filter"
          syncStatusesSet={syncStatusesSet}
          handleSyncStatusesSelection={handleSyncStatusesSelection}
          activeIntegration={activeIntegration}
        />,
      )
    }

    if (
      props.visibleFilters.includes(
        TransactionFilterOptions.ReceiptSyncStatus,
      ) &&
      showBulkErpAttachments
    ) {
      filters.push(
        <ReceiptSyncStatusFilter
          key="receipt-sync-status-filter"
          receiptAttachmentSyncStatusesSet={receiptAttachmentSyncStatusesSet}
          handleReceiptAttachmentSyncStatusesSelection={
            handleReceiptAttachmentSyncStatusesSelection
          }
        />,
      )
    }

    // Card User Filter
    if (props.visibleFilters.includes(TransactionFilterOptions.CardUser)) {
      filters.push(
        <CardUserFilter
          key="card-user-filter"
          recipientSet={recipientSet}
          handleRecipientSelection={handleRecipientSelection}
          setRecipientSet={setRecipientSet}
          recipientOptions={recipientOptions}
          userInfiniteScrollProps={userInfiniteScrollProps}
          defaultFilters={props.defaultFilters}
        />,
      )
    }

    // Departments Filter
    if (
      props.visibleFilters.includes(TransactionFilterOptions.Departments) &&
      departmentsOptions.length > 0
    ) {
      filters.push(
        <DepartmentsFilter
          key="departments-filter"
          departmentsSet={departmentsSet}
          handleDepartmentsSelection={handleDepartmentsSelection}
          setDepartmentsSet={setDepartmentsSet}
          departmentsOptions={departmentsOptions}
          defaultFilters={props.defaultFilters}
        />,
      )
    }

    // Account Filter
    if (props.visibleFilters.includes(TransactionFilterOptions.Account)) {
      filters.push(
        <AccountFilter
          key="account-filter"
          creditCardSet={creditCardSet}
          handleCreditCardSelection={handleCreditCardSelection}
          setCreditCardSet={setCreditCardSet}
          defaultFilters={props.defaultFilters}
        />,
      )
    }

    // Merchant Category Filter
    if (
      props.visibleFilters.includes(TransactionFilterOptions.MerchantCategory)
    ) {
      filters.push(
        <TransactionMerchantCategoryFilter
          key="merchant-category-filter"
          mccGroupSet={mccGroupSet}
          handleMccGroupSelection={handleMccGroupSelection}
          setMccGroupSet={setMccGroupSet}
          defaultFilters={props.defaultFilters}
        />,
      )
    }

    return filters
  }, [
    props.visibleFilters,
    dateType,
    startDateFilter,
    endDateFilter,
    props.filterValues,
    props.isTransactionsFetching,
    minClearingBillingCentsFilter,
    maxClearingBillingCentsFilter,
    statusSet,
    handleStatusSelection,
    originSet,
    handleOriginSelection,
    expenseCategoryStatusesSet,
    handleExpenseCategoryStatusesSelection,
    requiredExpenseCategories,
    expenseCategories,
    expenseCategoriesSet,
    handleExpenseCategoriesSelection,
    receiptStatusSet,
    handleReceiptStatusSelection,
    receiptRequired,
    setReceiptRequired,
    reviewStatusSet,
    handleReviewStatusesSelection,
    syncStatusesSet,
    handleSyncStatusesSelection,
    receiptAttachmentSyncStatusesSet,
    handleReceiptAttachmentSyncStatusesSelection,
    recipientSet,
    handleRecipientSelection,
    setRecipientSet,
    recipientOptions,
    userInfiniteScrollProps,
    props.defaultFilters,
    departmentsSet,
    handleDepartmentsSelection,
    setDepartmentsSet,
    departmentsOptions,
    creditCardSet,
    handleCreditCardSelection,
    setCreditCardSet,
    mccGroupSet,
    handleMccGroupSelection,
    setMccGroupSet,
    locale,
  ])

  return (
    <UtilityToolbar
      header={
        ![
          TransactionReportType.ExpenseReport,
          TransactionReportType.AccountingTransactions,
        ].includes(props.context) ? (
          <UtilityToolbarDatePicker
            datePickerTitle={
              <span
                css={css`
                  ${getFontCss(
                    14,
                    themeConstants.colors.font_primary,
                    styleConstants.fontFamilies.regular,
                  )}
                `}
              >
                <Trns
                  source={
                    props.filterValues.dateType ===
                    TransactionDateType.Transaction
                      ? 'Transaction Date'
                      : 'Clear Date'
                  }
                />
              </span>
            }
            tooltip={
              props.context !== TransactionReportType.NeedsApproval ? (
                <Tooltip openLeft={false} transformScale={1}>
                  <p>
                    <Trns
                      source={
                        '${boldedText} is the date on which transactions were authorized by the cardholder.'
                      }
                      expressions={{
                        boldedText: (
                          <span
                            css={css`
                              ${getFontCss(
                                12,
                                themeConstants.colors.font_primary,
                                styleConstants.fontFamilies.medium,
                              )};
                              ${getBreakpointDown(breakpointSizes.XSM)(css`
                                ${getFontCss(
                                  16,
                                  themeConstants.colors.font_primary,
                                  styleConstants.fontFamilies.medium,
                                )};
                              `)}
                            `}
                          >
                            <Trns source={'Transaction Date'} />
                          </span>
                        ),
                      }}
                    />
                    <span
                      css={css`
                        display: block;
                        margin-top: 10px;
                      `}
                    >
                      <Trns
                        source={
                          '${boldedText} is the date on which transactions were processed by the issuer (cleared), also known as the Settlement Date or Posted Date.'
                        }
                        expressions={{
                          boldedText: (
                            <span
                              css={css`
                                ${getFontCss(
                                  12,
                                  themeConstants.colors.font_primary,
                                  styleConstants.fontFamilies.medium,
                                )};
                                ${getBreakpointDown(breakpointSizes.XSM)(css`
                                  ${getFontCss(
                                    16,
                                    themeConstants.colors.font_primary,
                                    styleConstants.fontFamilies.medium,
                                  )};
                                `)}
                              `}
                            >
                              <Trns source={'Clear Date'} />
                            </span>
                          ),
                        }}
                      />
                    </span>
                    <span
                      css={css`
                        display: block;
                        margin-top: 10px;
                      `}
                    >
                      <Trns
                        source={'You can change the date type in "Filters".'}
                      />
                    </span>
                  </p>
                </Tooltip>
              ) : undefined
            }
            since={startDateFilter}
            until={endDateFilter}
            setSince={(val: Moment | undefined): void => {
              setStartDateFilter(val?.clone())
              props.handleFilterChange({ since: val?.clone() })
            }}
            setUntil={(val: Moment | undefined): void => {
              setEndDateFilter(val?.clone())
              props.handleFilterChange({ until: val?.clone() })
            }}
            disabled={props.context === TransactionReportType.NeedsApproval}
          />
        ) : undefined
      }
      searchProps={{
        value: searchFilter,
        onChange: (val: string): void => {
          setSearchFilter(val)
          props.handleFilterChange({ search: val })
        },
        placeholder: translateSingleString(
          locale,
          'Enter an amount, merchant name, or card name',
        ),
      }}
      userDropdownProps={
        ![
          TransactionReportType.MyTransactions,
          TransactionReportType.ExpenseReport,
        ].includes(props.context)
          ? {
              isCreatable: false,
              placeholder: translateSingleString(locale, 'All users'),
              chooseOption: (val: string): void => {
                const newSet = handleRecipientSelection(val)
                props.handleFilterChange({ recipient: newSet })
              },
              isMulti: true,
              selectedValues: recipientSet,
              optionGroups: [
                {
                  label: <Trns source={'Suggested'} />,
                  options: recipientOptions || [],
                },
              ],
              onResetClick: (): void => {
                setRecipientSet(
                  props.defaultFilters.recipient || new Set<string>(),
                )
                props.resetSingleFilter('recipient')
              },
              infiniteScrollProps: userInfiniteScrollProps,
              startAndKeepOpen: true,
              // use embedded style: true to update the dropdown style to match the new designs (which are still pending)
            }
          : undefined
      }
      filtersFlyoutProps={{
        title: translate('Transaction Filters'),
        onReset: props.resetFilters,
        loading: props.isTransactionsFetching,
        selectedFiltersCount: props.selectedFiltersCount,
        filters: (
          <>
            {filtersList.map((f, i) => (
              <div key={`filter_${i}`}>
                {f}
                {i !== filtersList?.length - 1 && (
                  <DividerLineDiv
                    css={css`
                      margin: 20px 0px;
                    `}
                  />
                )}
              </div>
            ))}
          </>
        ),
        onSave: () => {
          props.handleFilterChange({
            search: searchFilter,
            dateType: dateType,
            since: startDateFilter,
            until: endDateFilter,
            minClearingBillingCents: minClearingBillingCentsFilter,
            maxClearingBillingCents: maxClearingBillingCentsFilter,
            status: statusSet,
            source: originSet,
            expenseCategoryStatuses: expenseCategoryStatusesSet,
            expenseCategories: expenseCategoriesSet,
            receiptStatus: receiptStatusSet,
            receiptRequired: receiptRequired,
            syncStatuses: syncStatusesSet,
            receiptAttachmentSyncStatuses: receiptAttachmentSyncStatusesSet,
            recipient: recipientSet,
            departments: departmentsSet,
            creditCardId: creditCardSet,
            mccGroup: mccGroupSet,
            reviewStatus: reviewStatusSet,
          })
        },
        // reset UI filters on flyout open to avoid a race condition if we were to do it on flyout close
        onOpenFlyout: resetUIFiltersToCurrent,
      }}
      dateFilterDropdownProps={
        showAutomatedExpenseReview &&
        props.context === TransactionReportType.AccountingTransactions
          ? {
              buttonText: (
                <span
                  css={css`
                    @media (max-width: 1199px) {
                      display: none;
                    }
                  `}
                >{`${dateRangeText}`}</span>
              ),
              dropdown: (
                <DateFilterDropdown
                  showStatementOptions={creditCardSet.size === 1}
                  dateRangeType={dateRangeType}
                  handleSetType={handleSetType}
                  handleSetRange={handleSetRange}
                  handleSetSince={handleSetSince}
                  handleSetUntil={handleSetUntil}
                  customSince={startDateFilter}
                  customUntil={endDateFilter}
                  cycleId={selectedCreditCardCycle?.id}
                  currentStatement={currentStatement}
                  previousStatement={previousStatement}
                />
              ),
              image: <SvgCalendarNew />,
              testId: 'dateFilterDropdown',
              selected: dateRangeType !== undefined,
            }
          : undefined
      }
      creditCardDropdownProps={
        showAutomatedExpenseReview &&
        props.context === TransactionReportType.AccountingTransactions
          ? {
              selectedCreditCardIds: creditCardSet,
              onSelect: (
                val: DropdownValue<CreditCardDropdownOptionDetails>,
              ): void => {
                handleSetCreditCardId(val)
              },
              onReset: (): void => {
                resetCreditCardSet()
              },
              placeholder: 'All Card Accounts',
              location: location.pathname,
            }
          : undefined
      }
      utilityToolbarDropdownProps={
        ![
          TransactionReportType.MyTransactions,
          TransactionReportType.ExpenseReport,
        ].includes(props.context) &&
        receiptManagementEnabled &&
        props?.hasReceiptToolsDropdown &&
        organization !== undefined
          ? {
              buttonText: <Trns source={'Receipt Tools'} />,
              dropdown: (
                <ReceiptToolsDropdownUtilityToolbar
                  organization={organization}
                  hasReceiptRulePermissions={
                    !!user?.hasOrgBookkeeperPermissions
                  }
                />
              ),
              testId: 'receiptToolsDropdown',
            }
          : undefined
      }
      utilityToolbarDownloadProps={{
        onClick: props.handleDownloadClick,
      }}
    />
  )
}

export default TransactionsListFilter
